import * as React from 'react';
import { cva } from 'class-variance-authority';

import { mergeClassNames } from './class-names';

export type ButtonVariant = 'default' | 'accent' | 'ghost' | 'outline';
export type ButtonSize = 'default' | 'sm' | 'lg' | 'icon';

export type ButtonClassNameOptions = {
  className?: string;
  size?: ButtonSize;
  variant?: ButtonVariant;
};

export type ButtonProps = Omit<
  React.ComponentPropsWithRef<'button'>,
  'className'
> &
  ButtonClassNameOptions;

const buttonRecipe = cva(
  'inline-flex cursor-pointer items-center justify-center gap-2 whitespace-nowrap rounded-[var(--forma-radius-pill)] text-sm font-medium transition-all disabled:pointer-events-none disabled:cursor-not-allowed disabled:opacity-50 outline-none focus-visible:ring-2 focus-visible:ring-[var(--forma-focus-ring)] focus-visible:ring-offset-2 focus-visible:ring-offset-transparent',
  {
    variants: {
      variant: {
        default:
          'bg-[var(--forma-action-primary)] text-[var(--forma-action-primary-foreground)] shadow-lg hover:bg-[var(--forma-action-primary-hover)]',
        accent:
          'bg-[var(--forma-action-accent)] text-[var(--forma-action-accent-foreground)] shadow-[var(--forma-shadow-accent-button)] hover:bg-[var(--forma-action-accent-hover)] hover:shadow-[var(--forma-shadow-accent-button-hover)]',
        ghost:
          'bg-transparent text-[var(--forma-text-secondary)] hover:bg-[var(--forma-stone-100)] hover:text-[var(--forma-stone-800)]',
        outline:
          'border border-[var(--forma-border-default)] bg-[var(--forma-surface-glass)] text-[var(--forma-stone-700)] hover:bg-[var(--forma-surface-soft)]',
      },
      size: {
        default: 'h-11 px-5 py-2.5',
        sm: 'h-9 px-4',
        lg: 'h-12 px-6 text-base',
        icon: 'size-10 rounded-[var(--forma-radius-pill)]',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
    },
  },
);

export function buttonClassName({
  className,
  size,
  variant,
}: ButtonClassNameOptions = {}) {
  return mergeClassNames(buttonRecipe({ size, variant }), className);
}

export const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, ...props }, ref) => (
    <button
      className={buttonClassName({ className, variant, size })}
      ref={ref}
      {...props}
    />
  ),
);

Button.displayName = 'Button';
